/**
 * The rules behind the client-risk screen, kept pure so they can be read and
 * tested without a browser.
 *
 * Every one of them mirrors a rule the server already enforces. Nothing here is
 * a permission check — the server decides, always — these only decide what to
 * OFFER, so a manager is not invited to press a button that will be refused.
 */
import type {
  DestinationException,
  DestinationExceptionStatus,
  SecurityHoldEvent,
  SecurityHoldStatus,
} from '../../services/clientRisk.service';

/** What the client said was not theirs, in words a manager reads. */
export function describeTrigger(trigger: string | null): string {
  switch (trigger) {
    case 'bank_account_added':
      return 'A bank account was added to their profile';
    case null:
    case undefined:
      return 'Not recorded';
    default:
      return trigger.replace(/_/g, ' ');
  }
}

/** What a hold freezes, listed so nobody has to read the migration to find out. */
export const HOLD_FREEZES = [
  'Withdrawals and redemptions',
  'Payouts awaiting a destination',
  'Adding or removing a bank account',
  'Changing the phone number',
  'Changing beneficiaries',
] as const;

export interface HoldSummary {
  onHold: boolean;
  headline: string;
  detail: string;
  /** Newest first, as the API returns them. */
  events: SecurityHoldEvent[];
}

export function summariseHold(status: SecurityHoldStatus | null): HoldSummary | null {
  if (status === null) return null;
  if (!status.on_hold) {
    return {
      onHold: false,
      headline: 'No hold',
      detail:
        status.events.length > 0
          ? 'This client has been held before — the history is below.'
          : 'This client has never been held.',
      events: status.events,
    };
  }
  return {
    onHold: true,
    headline: 'Money-out is frozen',
    detail: `${describeTrigger(status.trigger)}, and they said it was not them.`,
    events: status.events,
  };
}

const _EXCEPTION_LABEL: Record<DestinationExceptionStatus, string> = {
  proposed: 'Waiting for a decision',
  approved: 'Approved',
  rejected: 'Rejected',
  expired: 'Expired',
};

export function describeExceptionStatus(status: DestinationExceptionStatus): string {
  return _EXCEPTION_LABEL[status] ?? status;
}

/**
 * Whether this viewer may decide this exception.
 *
 * The proposer may never be the decider — that is the whole point of an
 * exception to the return-to-source rule, and both the service and a database
 * CHECK refuse it. Offering the button anyway would teach managers that the
 * screen lies.
 */
export function canDecide(
  exception: DestinationException,
  viewer: { id: string; role: string | null },
): boolean {
  if (exception.status !== 'proposed') return false;
  if (viewer.role !== 'manager' && viewer.role !== 'super_admin') return false;
  return exception.proposed_by !== viewer.id;
}

/** Why the decide button is not offered, said plainly rather than hidden. */
export function whyCannotDecide(
  exception: DestinationException,
  viewer: { id: string; role: string | null },
): string | null {
  if (exception.status !== 'proposed') return null;
  if (viewer.role !== 'manager' && viewer.role !== 'super_admin') {
    return 'A manager decides this.';
  }
  if (exception.proposed_by === viewer.id) {
    return 'You proposed this one. Someone else has to decide it.';
  }
  return null;
}

export interface CountryRow {
  code: string;
  name: string;
  /** 'blocked' · 'removal_pending' (the partner dropped it, we have not) · 'lifted'. */
  state: 'blocked' | 'removal_pending' | 'lifted';
  verified: string | null;
}

/**
 * Three states, not two. A country the banking partner stopped listing is NOT
 * unblocked: it waits for a super_admin to lift it by hand, because a parser
 * must never be what re-opens a sanctioned country.
 */
export function classifyCountry(row: {
  country_code: string;
  country_name: string;
  is_active: boolean;
  removal_pending_since: string | null;
  last_verified_at: string | null;
}): CountryRow {
  let state: CountryRow['state'] = 'lifted';
  if (row.is_active) {
    state = row.removal_pending_since ? 'removal_pending' : 'blocked';
  }
  return {
    code: row.country_code,
    name: row.country_name,
    state,
    verified: row.last_verified_at,
  };
}

/** A sync that changed nothing is the normal day; say so rather than showing zeros. */
export function describeSyncRun(run: {
  status: string;
  added: number | null;
  removal_pending: number | null;
  parsed_count: number | null;
}): string {
  if (run.status === 'unreachable') return 'Could not read the partner’s page — nothing changed.';
  if (run.status === 'suspicious') {
    return 'The partner’s page could not be read with confidence — nothing was applied.';
  }
  const added = run.added ?? 0;
  const pending = run.removal_pending ?? 0;
  if (added === 0 && pending === 0) {
    return `No change — ${run.parsed_count ?? 0} countries read.`;
  }
  const parts: string[] = [];
  if (added > 0) parts.push(`${added} newly blocked`);
  if (pending > 0) parts.push(`${pending} no longer listed by the partner`);
  return parts.join(' · ');
}

export const MIN_BLOCK_REASON = 10;

/** QMI's own countries are told apart from the partner's on the list. */
export function isQmiCountry(row: { source: string }): boolean {
  return row.source === 'qmi';
}

/** The add form is offered as ready only when the server would not refuse it for shape. */
export function addCountryReady(form: {
  code: string;
  reason: string;
  mfaCode: string;
  busy: boolean;
}): boolean {
  return (
    /^[A-Z]{2}$/.test(form.code) &&
    form.reason.trim().length >= MIN_BLOCK_REASON &&
    form.mfaCode.trim().length >= 6 &&
    !form.busy
  );
}

/** An empty link is no link; the server keeps it optional. */
export function cleanSourceUrl(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed === '' ? null : trimmed;
}
